import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { privilegedKeyDetected, privilegedKeyMessage } from './state/auth.ts';
import 'katex/dist/katex.min.css';
import '@yuan-math/ui/theme.css';
import './styles/app.css';
import { App } from './App.tsx';
import { LearnerProvider } from './state/LearnerContext.tsx';
import { AuthProviderComponent } from './state/AuthContext.tsx';

const container = document.getElementById('root');
if (!container) throw new Error('Root element missing');

/**
 * Boot.
 *
 * A privileged Supabase key reaching the browser halts startup deliberately —
 * but a blank page is a terrible way to learn why, so an explanation is
 * rendered instead of leaving whoever deployed it hunting through the console.
 *
 * Checked as a flag rather than caught as an exception: a module-scope throw
 * happens during import evaluation, before any try/catch here could run.
 */
if (privilegedKeyDetected) {
  renderKeyWarning(container, privilegedKeyMessage);
} else {
  createRoot(container).render(
    <StrictMode>
      <BrowserRouter>
        <AuthProviderComponent>
          <LearnerProvider>
            <App />
          </LearnerProvider>
        </AuthProviderComponent>
      </BrowserRouter>
    </StrictMode>,
  );
}

/**
 * Written as plain DOM rather than React: the failure may have happened before
 * React could mount, and an error page that depends on the thing that failed is
 * not an error page.
 */
function renderKeyWarning(root: HTMLElement, message: string): void {
  root.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.style.cssText =
    'max-width:44rem;margin:12vh auto;padding:0 1.5rem;font-family:system-ui,-apple-system,sans-serif;line-height:1.6;color:#1a1a18;';

  const heading = document.createElement('h1');
  heading.textContent = 'This build is using a privileged Supabase key';
  heading.style.cssText = 'font-size:1.75rem;margin:0 0 1rem;line-height:1.25;';

  const detail = document.createElement('p');
  detail.textContent = message;
  detail.style.cssText = 'margin:0 0 1.5rem;';

  const box = document.createElement('div');
  box.style.cssText = 'background:#fbeceA;border-left:3px solid #a8402f;border-radius:8px;padding:1rem 1.25rem;';
  box.innerHTML =
    '<p style="margin:0 0 .5rem;font-weight:600;">What to do</p>' +
    '<ol style="margin:0;padding-left:1.25rem;">' +
    '<li>In Supabase, open Settings → API and copy the <strong>publishable</strong> key ' +
    '(labelled &ldquo;anon public&rdquo; on older projects).</li>' +
    '<li>Replace <code>VITE_SUPABASE_ANON_KEY</code> with it, and rebuild.</li>' +
    '<li>If the secret key has already been deployed anywhere, <strong>revoke it</strong> — ' +
    'it may have been served to visitors.</li>' +
    '</ol>';

  const note = document.createElement('p');
  note.style.cssText = 'margin:1.5rem 0 0;color:#5c5a55;font-size:.9375rem;';
  note.textContent =
    'The rest of the platform is unaffected: lessons, the Math Map and practice all work without any key at all. ' +
    'Startup was stopped only to keep this from going unnoticed.';

  wrap.append(heading, detail, box, note);
  root.append(wrap);
}
